// The ONE toast every refused upload shows (ISP-9999).
//
// Split from utils/storageLimit.js on purpose: that module is pure decision
// logic with no React, so it can be unit-tested without a renderer. This file
// is the thin presentation shell — the sentence plus the way out.
//
// Every surface that refuses an upload calls THIS, so the wording, the Upgrade
// affordance and the toast id are identical everywhere. A per-surface toast is
// how the app ends up telling the same user three different stories about the
// same limit.
import React from "react";
import { toast } from "react-toastify";
import { storageFullMessage, storageLimitErrorMessage } from "./storageLimit";

// One id, so N rapid attempts (a multi-file drop, a double click) collapse to
// ONE toast instead of stacking a wall of identical messages.
export const STORAGE_FULL_TOAST_ID = "storage-limit-exceeded";

// Where "Upgrade" goes. The plans page is the app's existing upgrade surface
// and works from every route, including the ones that never mount the redux
// upgrade popup.
export const UPGRADE_HREF = "/subscription";

export const StorageFullToastBody = ({ message, onUpgrade }) => (
  <span>
    {message}{" "}
    <a
      href={UPGRADE_HREF}
      data-testid="storage-full-upgrade-link"
      onClick={(event) => {
        if (onUpgrade) {
          // A caller that owns the in-app upgrade popup handles it inline
          // instead of a full navigation that would lose unsaved builder state.
          event.preventDefault();
          onUpgrade();
        }
        toast.dismiss(STORAGE_FULL_TOAST_ID);
      }}
    >
      Upgrade
    </a>
  </span>
);

/**
 * Show the shared "you are out of storage" toast.
 *
 * @param {object} usage the `useStorageUsage` view-model, for the "X of Y".
 * @param {function} [onUpgrade] optional in-app upgrade handler.
 */
export const showStorageFullToast = (usage, onUpgrade) =>
  toast.error(
    <StorageFullToastBody message={storageFullMessage(usage)} onUpgrade={onUpgrade} />,
    { toastId: STORAGE_FULL_TOAST_ID }
  );

/**
 * Show the same toast in response to the backend's 402, preferring the
 * server's own numbers when it sent them.
 */
export const showStorageLimitErrorToast = (error, usage, onUpgrade) =>
  toast.error(
    <StorageFullToastBody
      message={storageLimitErrorMessage(error, usage)}
      onUpgrade={onUpgrade}
    />,
    { toastId: STORAGE_FULL_TOAST_ID }
  );
